import React, { useState, useContext, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { ThemeContext } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar';

// Notification Toast Components
const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  min-width: 300px;
  background-color: ${props => props.$type === 'success' ? '#2ecc71' : '#e74c3c'};
  color: white;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 9999;
  animation: slideIn 0.3s ease-out forwards;
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
`;

const NotificationMessage = styled.div`
  flex: 1;
  font-weight: 500;
`;

const CloseNotificationButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  margin-left: 10px;
  opacity: 0.8;
  transition: opacity 0.2s;
  &:hover { opacity: 1; }
`;

const AdminLaporKTSPage = ({ isSidebarOpen, onSidebarToggle }) => {
  // Admin users can access both forms - always show both tabs
  const isGuruBengkel = true;
  const isGuruTeori = true;
  const isGuruBk = true;
  const isMultiRole = true;

  const { darkMode } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [accordionState, setAccordionState] = useState({
    suratIjinOpen: false,
    laporanOpen: true, // Open by default since we're in a laporan page
    masterDataOpen: false
  });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showSubSuggestions, setShowSubSuggestions] = useState(false);
  const [showStudentSuggestions, setShowStudentSuggestions] = useState(false);
  const [showTeoriStudentSuggestions, setShowTeoriStudentSuggestions] = useState(false);
  const [showBkStudentSuggestions, setShowBkStudentSuggestions] = useState(false);
  const [showJenisKetidaksesuaianSuggestions, setShowJenisKetidaksesuaianSuggestions] = useState(false);
  const [showTeoriJenisKetidaksesuaianSuggestions, setShowTeoriJenisKetidaksesuaianSuggestions] = useState(false);
  const [showBkJenisKetidaksesuaianSuggestions, setShowBkJenisKetidaksesuaianSuggestions] = useState(false);
  const suggestionRef = useRef(null);
  const subSuggestionRef = useRef(null);
  const studentSuggestionRef = useRef(null);
  const teoriStudentSuggestionRef = useRef(null);
  const bkStudentSuggestionRef = useRef(null);
  
  // API Data States
  const [studentOptions, setStudentOptions] = useState([]);
  const [ktsOptions, setKtsOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // State for multi-role form switching
  const [formMode, setFormMode] = useState('bengkel');

  // Get auth token
  const getAuthToken = () => {
    const token = localStorage.getItem('token');
    console.log('Retrieved token from localStorage:', token ? `${token.substring(0, 20)}...` : 'null');
    return token;
  };

  // API base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';

  // Fetch students from API
  const fetchStudents = async () => {
    try {
      const token = getAuthToken();
      console.log('Token for students API:', token ? 'Token exists' : 'No token found');
      
      if (!token) {
        console.error('No authentication token found. Please login again.');
        navigate('/login');
        return;
      }
      
      const response = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/api/admin/students`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Students API response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStudentOptions(data.data);
        }
      } else if (response.status === 403) {
        console.error('Access forbidden. Token may be invalid or expired.');
        // Optionally redirect to login
        // navigate('/login');
      } else {
        console.error(`Error fetching students: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  // Fetch KTS options from API
  const fetchKtsOptions = async (type) => {
    if (!type) return; // Do not fetch if type is not specified

    try {
      const token = getAuthToken();
      console.log('Token for KTS options API:', token ? 'Token exists' : 'No token found');
      console.log('Fetching KTS options for type:', type);
      
      if (!token) {
        console.error('No authentication token found. Please login again.');
        navigate('/login');
        return;
      }
      
      const response = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/api/admin/kts-options?type=${type}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('KTS options API response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setKtsOptions(data.data);
        } else {
          console.error('Failed to fetch KTS options:', data.message);
        }
      } else if (response.status === 403) {
        console.error('Access forbidden. Token may be invalid or expired.');
        // Optionally redirect to login
        // navigate('/login');
      } else {
        console.error(`Error fetching KTS options: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error fetching KTS options:', error);
    }
  };

  // Check admin access
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    const userRole = localStorage.getItem('userRole');
    
    if (!isAuthenticated || userRole !== 'admin_user') {
      navigate('/login');
      return;
    }
    
    // If we get here, user is authenticated admin
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        console.error('Error parsing user data:', error);
        navigate('/login');
      }
    }
  }, [navigate]);

  // Load data on component mount
  useEffect(() => {
    fetchStudents();
  }, []);

  // Fetch KTS options based on form mode
  useEffect(() => {
    let typeToFetch;

    if (formMode === 'bengkel') {
      typeToFetch = 'praktek';
    } else {
      typeToFetch = 'teori';
    }

    if (typeToFetch) {
      fetchKtsOptions(typeToFetch);
    }
  }, [formMode]);

  // Get filtered KTS options based on search query
  const getFilteredKtsOptions = (searchQuery) => {
    if (!searchQuery) return ktsOptions;
    return ktsOptions.filter(option => 
      option.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // Get pelanggaran details by ID for auto-populating fields
  const getPelanggaranById = (pelanggaranId) => {
    return ktsOptions.find(option => option.id === parseInt(pelanggaranId));
  };

  // Auto-populate jam and section based on selected pelanggaran
  const handlePelanggaranSelection = (pelanggaran, formType) => {
    if (!pelanggaran) return;

    // Determine jam value based on type
    const jamValue = pelanggaran.jam_kompensasi;
    const sectionValue = pelanggaran.section;
    const jamKersosValue = pelanggaran.jam_kersos || '';
    const poinSikapValue = pelanggaran.poin_sikap || '';
    const konsekuensiTambahanValue = pelanggaran.konsekuensi_tambahan || '';

    // Update appropriate form state
    if (formType === 'kompensasi') {
      setKompensasiData(prev => ({
        ...prev,
        jenisKetidaksesuaian: pelanggaran.description,
        jenisKetidaksesuaianSearch: pelanggaran.description,
        kts_id: pelanggaran.id,
        total_hour: jamValue.toString(),
        total_hour_kersos: jamKersosValue.toString(),
        appreciation_point: poinSikapValue.toString(),
        additional_consequence: konsekuensiTambahanValue,
        section: sectionValue
      }));
    } else if (formType === 'teori') {
      setTeoriData(prev => ({
        ...prev,
        jenisKetidaksesuaian: pelanggaran.description,
        jenisKetidaksesuaianSearch: pelanggaran.description,
        kts_id: pelanggaran.id,
        total_hour: jamValue.toString(),
        total_hour_kersos: jamKersosValue.toString(),
        appreciation_point: poinSikapValue.toString(),
        additional_consequence: konsekuensiTambahanValue,
        section: sectionValue,
      }));
    } else if (formType === 'bk') {
      setBkData(prev => ({
        ...prev,
        jenisKetidaksesuaian: pelanggaran.description,
        jenisKetidaksesuaianSearch: pelanggaran.description,
        kts_id: pelanggaran.id,
        total_hour: jamValue.toString(),
        total_hour_kersos: jamKersosValue.toString(),
        appreciation_point: poinSikapValue.toString(),
        additional_consequence: konsekuensiTambahanValue,
        section: sectionValue,
      }));
    }
  };

  // --- Per Tab State ---
  const [activeTab, setActiveTab] = useState('kompensasi'); // For Guru Bengkel

  // Kompensasi Form State (Bengkel)
  const [kompensasiData, setKompensasiData] = useState({
    category: 'kompensasi',
    selectedStudents: [],
    studentSearch: '',
    jenisKetidaksesuaian: '',
    jenisKetidaksesuaianSearch: '', // For auto-suggestion
    kts_id: '',
    total_hour: '',
    total_hour_kersos: '',
    appreciation_point: '',
    additional_consequence: '',
    section: '',
    shift: '',
    description: ''
  });

  // Ketidakhadiran Form State (Bengkel)
  const [ketidakhadiranData, setKetidakhadiranData] = useState({
    category: 'ketidakhadiran',
    selectedStudents: [],
    studentSearch: '',
    jenisKetidakhadiran: '',
    absent_type: '',
    total_hour: '',
    total_hour_kersos: '',
    appreciation_point: '',
    section: '',
    shift: '',
    description: ''
  });

  // Guru Teori Form State (simplified)
  const [teoriData, setTeoriData] = useState({
    category: 'kompensasi', // Default category for teori
    selectedStudents: [],
    studentSearch: '',
    jenisKetidaksesuaian: '',
    jenisKetidaksesuaianSearch: '', // For auto-suggestion
    kts_id: '',
    total_hour: '',
    total_hour_kersos: '',
    appreciation_point: '',
    additional_consequence: '',
    section: '',
    description: ''
  });

  // Teori Ketidakhadiran Form State
  const [teoriKetidakhadiranData, setTeoriKetidakhadiranData] = useState({
    category: 'ketidakhadiran',
    selectedStudents: [],
    studentSearch: '',
    jenisKetidakhadiran: '',
    absent_type: '',
    total_hour: '',
    total_hour_kersos: '',
    appreciation_point: '',
    section: '-',
    description: ''
  });

  // Guru BK Form State (simplified)
  const [bkData, setBkData] = useState({
    category: 'kompensasi', // Default category for BK
    selectedStudents: [],
    studentSearch: '',
    jenisKetidaksesuaian: '',
    jenisKetidaksesuaianSearch: '', // For auto-suggestion
    kts_id: '',
    total_hour: '',
    total_hour_kersos: '',
    appreciation_point: '',
    additional_consequence: '',
    section: '',
    description: ''
  });

  // Notification state
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ show: false, message: '', type: 'success' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification.show]);

  // Submit form data to API with admin_user_id
  const submitKtsReport = async (formData, submittedClassType) => {
    try {
      setSubmitting(true);
      const token = getAuthToken();
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      
      const payload = {
        class_type: submittedClassType,
        category: formData.category,
        student_ids: formData.selectedStudents,
        description: formData.description,
        admin_user_id: userData.id, // Store admin user ID
        teacher_id: null, // Set teacher_id to null for admin submissions
        ...(formData.kts_id && { kts_id: formData.kts_id }),
        ...(formData.absent_type && { absent_type: formData.absent_type }),
        ...(formData.jenisKetidakhadiran && { absent_type: formData.jenisKetidakhadiran }),
        ...(formData.total_hour && { total_hour: parseFloat(formData.total_hour) }),
        ...(formData.total_hour_kersos && { total_hour_kersos: parseFloat(formData.total_hour_kersos) }),
        ...(formData.appreciation_point && { appreciation_point: parseFloat(formData.appreciation_point) }),
        ...(formData.additional_consequence && { additional_consequence: formData.additional_consequence }),
        ...(formData.section && { section: formData.section }),
        ...(formData.shift && { shift: formData.shift })
      };

      const response = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/api/admin/kts-reports`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setNotification({ show: true, message: 'Laporan KTS berhasil disimpan!', type: 'success' });
        // Reset form based on form mode
        if (formMode === 'bengkel') {
          if (activeTab === 'kompensasi') {
            setKompensasiData({
              category: 'kompensasi',
              selectedStudents: [],
              studentSearch: '',
              jenisKetidaksesuaian: '',
              jenisKetidaksesuaianSearch: '',
              kts_id: '',
              total_hour: '',
              total_hour_kersos: '',
              appreciation_point: '',
              additional_consequence: '',
              section: '',
              shift: '',
              description: ''
            });
          } else {
            setKetidakhadiranData({
              category: 'ketidakhadiran',
              selectedStudents: [],
              studentSearch: '',
              jenisKetidakhadiran: '',
              absent_type: '',
              total_hour: '',
              total_hour_kersos: '',
              appreciation_point: '',
              section: '',
              shift: '',
              description: ''
            });
          }
        } else if (formMode === 'teori') {
          if (formData.category === 'kompensasi') {
            setTeoriData({
              category: 'kompensasi',
              selectedStudents: [],
              studentSearch: '',
              jenisKetidaksesuaian: '',
              jenisKetidaksesuaianSearch: '',
              kts_id: '',
              total_hour: '',
              total_hour_kersos: '',
              appreciation_point: '',
              additional_consequence: '',
              section: '',
              description: ''
            });
          } else {
            setTeoriKetidakhadiranData({
              category: 'ketidakhadiran',
              selectedStudents: [],
              studentSearch: '',
              jenisKetidakhadiran: '',
              absent_type: '',
              total_hour: '',
              total_hour_kersos: '',
              appreciation_point: '',
              section: '',
              description: ''
            });
          }
        }
      } else {
        setNotification({ show: true, message: `Error: ${data.message || 'Gagal menyimpan laporan'}`, type: 'error' });
      }
    } catch (error) {
      console.error('Error submitting KTS report:', error);
      setNotification({ show: true, message: 'Terjadi kesalahan saat menyimpan laporan', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle form submissions
  const handleKompensasiSubmit = () => {
    if (!kompensasiData.kts_id || kompensasiData.selectedStudents.length === 0 || !kompensasiData.description) {
      setNotification({ show: true, message: 'Mohon lengkapi semua field yang diperlukan', type: 'error' });
      return;
    }
    submitKtsReport(kompensasiData, 'praktek');
  };

  const handleKetidakhadiranSubmit = () => {
    if (ketidakhadiranData.selectedStudents.length === 0 || !ketidakhadiranData.jenisKetidakhadiran || !ketidakhadiranData.total_hour || !ketidakhadiranData.section || !ketidakhadiranData.shift || !ketidakhadiranData.description) {
      setNotification({ show: true, message: 'Mohon lengkapi semua field yang diperlukan', type: 'error' });
      return; 
    }
    submitKtsReport(ketidakhadiranData, 'praktek');
  };

  const handleTeoriSubmit = () => {
    if (!teoriData.kts_id || teoriData.selectedStudents.length === 0 || !teoriData.description) {
      setNotification({ show: true, message: 'Mohon lengkapi semua field yang diperlukan', type: 'error' });
      return;
    }
    submitKtsReport(teoriData, 'teori');
  };

  const handleTeoriKetidakhadiranSubmit = () => {
    if (teoriKetidakhadiranData.selectedStudents.length === 0 || !teoriKetidakhadiranData.jenisKetidakhadiran || !teoriKetidakhadiranData.total_hour || !teoriKetidakhadiranData.section || !teoriKetidakhadiranData.description) {
      setNotification({ show: true, message: 'Mohon lengkapi semua field yang diperlukan', type: 'error' });
      return;
    }
    submitKtsReport(teoriKetidakhadiranData, 'teori');
  };

  const handleBkSubmit = () => {
    if (!bkData.kts_id || bkData.selectedStudents.length === 0 || !bkData.description) {
      setNotification({ show: true, message: 'Mohon lengkapi semua field yang diperlukan', type: 'error' });
      return;
    }
    submitKtsReport(bkData, 'teori');
  };

  // Helper function to add student to selected list
  const addStudentToList = (student, formType) => {
    const studentId = student.id;
    
    if (formType === 'kompensasi') {
      if (!kompensasiData.selectedStudents.includes(studentId)) {
        setKompensasiData({
          ...kompensasiData,
          selectedStudents: [...kompensasiData.selectedStudents, studentId],
          studentSearch: ''
        });
      }
    } else if (formType === 'ketidakhadiran') {
      if (!ketidakhadiranData.selectedStudents.includes(studentId)) {
        setKetidakhadiranData({
          ...ketidakhadiranData,
          selectedStudents: [...ketidakhadiranData.selectedStudents, studentId],
          studentSearch: ''
        });
      }
    } else if (formType === 'teori') {
      if (activeTab === 'kompensasi') {
        if (!teoriData.selectedStudents.includes(studentId)) {
          setTeoriData({
            ...teoriData,
            selectedStudents: [...teoriData.selectedStudents, studentId],
            studentSearch: ''
          });
        }
      } else { // ketidakhadiran
        if (!teoriKetidakhadiranData.selectedStudents.includes(studentId)) {
          setTeoriKetidakhadiranData({
            ...teoriKetidakhadiranData,
            selectedStudents: [...teoriKetidakhadiranData.selectedStudents, studentId],
            studentSearch: ''
          });
        }
      }
    } else if (formType === 'bk') {
      if (!bkData.selectedStudents.includes(studentId)) {
        setBkData({
          ...bkData,
          selectedStudents: [...bkData.selectedStudents, studentId],
          studentSearch: ''
        });
      }
    }
    
    // Hide suggestions
    setShowStudentSuggestions(false);
    setShowTeoriStudentSuggestions(false);
    setShowBkStudentSuggestions(false);
  };

  // Helper function to remove student from selected list
  const removeStudentFromList = (studentId, formType) => {
    if (formType === 'kompensasi') {
      setKompensasiData({
        ...kompensasiData,
        selectedStudents: kompensasiData.selectedStudents.filter(id => id !== studentId)
      });
    } else if (formType === 'ketidakhadiran') {
      setKetidakhadiranData({
        ...ketidakhadiranData,
        selectedStudents: ketidakhadiranData.selectedStudents.filter(id => id !== studentId)
      });
    } else if (formType === 'teori') {
      if (activeTab === 'kompensasi') {
        setTeoriData({
          ...teoriData,
          selectedStudents: teoriData.selectedStudents.filter(id => id !== studentId)
        });
      } else {
        setTeoriKetidakhadiranData({
          ...teoriKetidakhadiranData,
          selectedStudents: teoriKetidakhadiranData.selectedStudents.filter(id => id !== studentId)
        });
      }
    } else if (formType === 'bk') {
      setBkData({
        ...bkData,
        selectedStudents: bkData.selectedStudents.filter(id => id !== studentId)
      });
    }
  };

  // Get student name by ID
  const getStudentNameById = (studentId) => {
    const student = studentOptions.find(s => s.id === studentId);
    return student ? `${student.name} (${student.class})` : 'Unknown Student';
  };

  // --- Helper: Parse class grade from selected student(s) ---
  // Calculates class grade (X, XI, XII) using the first selected student.
  // Robust parsing handles formats like "NAME (XTP6)", "XII TKR 3", "XI-TP6".
  // Fallback: returns null when data is missing or malformed.
  const inferClassGradeFromSelected = (selectedStudents) => {
    if (!Array.isArray(selectedStudents) || selectedStudents.length === 0) return null;
    const firstId = selectedStudents[0];
    const student = studentOptions.find(s => s.id === firstId);
    const raw = (student?.class || getStudentNameById(firstId) || '').toString();
    const s = raw.toUpperCase();
    if (s.includes('XII')) return 'XII';
    if (s.includes('XI')) return 'XI';
    // Detect grade X only when XI/XII are not present
    if (!s.includes('XI') && !s.includes('XII') && s.includes('X')) return 'X';
    return null;
  };

  // --- Helper: Compute practical absence total hours ---
  // For absence type Sakit/Ijin in Form Praktek (ketidakhadiran):
  // - XI/XII → -7 hours
  // - X      → -5 hours
  // Fallback to -7 when class cannot be determined to preserve prior behavior.
  const computePraktekTotalHourForAbsence = (absentType, selectedStudents) => {
    const t = (absentType || '').toLowerCase();
    if (t !== 'sakit' && t !== 'ijin') return '';
    const grade = inferClassGradeFromSelected(selectedStudents);
    if (grade === 'X') return '-5';
    if (grade === 'XI' || grade === 'XII') return '-7';
    return '-7';
  };

  // Normalize avatar URL
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
  const avatarUrl = user?.avatar_url
    ? user.avatar_url.startsWith('http')
      ? user.avatar_url
      : `${baseUrl}${user.avatar_url.startsWith('/') ? '' : '/'}${user.avatar_url}`
    : '';

  const adminData = {
    name: user?.name || '',
    position: user?.role || '',
    avatarUrl: avatarUrl,
  };

  // --- RETURN: Render Forms Based on Role ---
  return (
    <PageContainer>
      {notification.show && (
        <NotificationToast $type={notification.type}>
          <NotificationMessage>{notification.message}</NotificationMessage>
          <CloseNotificationButton onClick={() => setNotification({ show: false, message: '', type: 'success' })}>×</CloseNotificationButton>
        </NotificationToast>
      )}
      <PageHeader>
        <HeaderContent>
          <div>
            <h1>Lapor Ketidaksesuaian (Admin)</h1>
            <Subtitle>Lapor ketidaksesuaian siswa</Subtitle>
          </div>
        </HeaderContent>
      </PageHeader>

      <DashboardGrid>
        <SidebarContainer $isOpen={isSidebarOpen}>
          <SidebarOverlay $isOpen={isSidebarOpen} onClick={() => onSidebarToggle && onSidebarToggle()} />
          <SidebarContent $isOpen={isSidebarOpen}>
            <SidebarHeader>
              <h3>Menu Admin</h3>
              <CloseButton onClick={() => onSidebarToggle && onSidebarToggle()}>&times;</CloseButton>
            </SidebarHeader>
            <AdminSidebar 
              user={adminData}
              accordionState={accordionState} 
              setAccordionState={setAccordionState}
              onSidebarToggle={onSidebarToggle}
            />
          </SidebarContent>
        </SidebarContainer>
        
        <MainSection>
          <ContentSection>
          
          <FormContent>
            {/* Admin can access both forms */}
            <TypeToggleContainer>
              <TypeToggleButtonGroup>
                <TypeToggleButton active={formMode === 'bengkel'} onClick={() => setFormMode('bengkel')}>
                  FORM PRAKTEK
                </TypeToggleButton>
                <TypeToggleButton active={formMode === 'teori'} onClick={() => setFormMode('teori')}>
                  FORM TEORI
                </TypeToggleButton>
              </TypeToggleButtonGroup>
            </TypeToggleContainer>

            {/* Form Praktek */}
            {formMode === 'bengkel' && (
              <>
                <TypeToggleContainer>
                  <TypeToggleButtonGroup>
                    <TypeToggleButton active={activeTab === 'kompensasi'} onClick={() => setActiveTab('kompensasi')}>KOMPENSASI</TypeToggleButton>
                    <TypeToggleButton active={activeTab === 'ketidakhadiran'} onClick={() => setActiveTab('ketidakhadiran')}>KETIDAKHADIRAN</TypeToggleButton>
                  </TypeToggleButtonGroup>
                </TypeToggleContainer>
                
                {activeTab === 'kompensasi' && (
                  <>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Nama Siswa</label>
                      <input
                        type="text"
                        placeholder="Ketik & cari nama siswa..."
                        value={kompensasiData.studentSearch || ''}
                        onChange={e => {
                          const value = e.target.value;
                          setKompensasiData({ ...kompensasiData, studentSearch: value });
                          setShowStudentSuggestions(true);
                        }}
                        onFocus={() => setShowStudentSuggestions(true)}
                        style={{ marginBottom: 8 }}
                      />
                      {showStudentSuggestions && kompensasiData.studentSearch && (
                        <SuggestionsList theme={darkMode ? 'dark' : 'light'}>
                          {studentOptions.filter(opt =>
                            opt.name.toLowerCase().includes(kompensasiData.studentSearch.toLowerCase()) &&
                            !kompensasiData.selectedStudents.includes(opt.id)
                          ).map(opt => (
                            <StudentSuggestionItem
                              key={opt.id}
                              theme={darkMode ? 'dark' : 'light'}
                              onClick={() => {
                                addStudentToList(opt, 'kompensasi');
                              }}
                            >
                              <span className="student-name">{opt.name}</span>{' '}
                              <span className="student-info">({opt.class})</span>
                            </StudentSuggestionItem>
                          ))}
                        </SuggestionsList>
                      )}
                      <SelectedStudentsContainer>
                        {kompensasiData.selectedStudents.map((studentId, idx) => (
                          <SelectedStudentTag key={studentId}>
                            <span style={{ fontWeight: 500 }}>{getStudentNameById(studentId)}</span>
                            <button type="button" onClick={() => {
                              removeStudentFromList(studentId, 'kompensasi');
                            }}>×</button>
                          </SelectedStudentTag>
                        ))}
                      </SelectedStudentsContainer>
                    </FormGroup>
                    
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Jenis Ketidaksesuaian</label>
                      <input
                        type="text"
                        placeholder="Ketik & cari jenis ketidaksesuaian..."
                        value={kompensasiData.jenisKetidaksesuaianSearch || ''}
                        onChange={e => {
                          const value = e.target.value;
                          setKompensasiData({ 
                            ...kompensasiData, 
                            jenisKetidaksesuaianSearch: value,
                            jenisKetidaksesuaian: value,
                            kts_id: '',
                            total_hour: '',
                            total_hour_kersos: '',
                            appreciation_point: '',
                            additional_consequence: '',
                            section: ''
                          });
                          setShowJenisKetidaksesuaianSuggestions(true);
                        }}
                        onFocus={() => setShowJenisKetidaksesuaianSuggestions(true)}
                        required
                      />
                      {showJenisKetidaksesuaianSuggestions && kompensasiData.jenisKetidaksesuaianSearch && (
                        <SuggestionsList theme={darkMode ? 'dark' : 'light'}>
                          {getFilteredKtsOptions(kompensasiData.jenisKetidaksesuaianSearch).map(opt => (
                            <SuggestionItem
                              key={opt.id}
                              theme={darkMode ? 'dark' : 'light'}
                              onClick={() => {
                                handlePelanggaranSelection(opt, 'kompensasi');
                                setShowJenisKetidaksesuaianSuggestions(false);
                              }}
                            >
                              {opt.description}
                            </SuggestionItem>
                          ))}
                        </SuggestionsList>
                      )}
                    </FormGroup>
                    
                    <FormRow>
                      <FormGroup theme={darkMode ? 'dark' : 'light'}>
                        <label>Section</label>
                        <input
                          type="text"
                          value={kompensasiData.section}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                          placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                        />
                      </FormGroup>
                      <FormGroup theme={darkMode ? 'dark' : 'light'}>
                        <label>Konsekuensi Tambahan</label>
                        <input
                          type="text"
                          value={kompensasiData.additional_consequence}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                          placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                        />
                      </FormGroup>
                    </FormRow>
                    
                    <FormRow>
                      <FormGroup theme={darkMode ? 'dark' : 'light'}>
                        <label>Jumlah Jam Lembur</label>
                        <input
                          type="text"
                          value={kompensasiData.total_hour}
                         
                          placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                        />
                      </FormGroup>
                      <FormGroup theme={darkMode ? 'dark' : 'light'}>
                        <label>Jumlah Jam Kersos</label>
                        <input
                          type="text"
                          value={kompensasiData.total_hour_kersos}
                         
                          placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                        />
                      </FormGroup>
                    </FormRow>
                    
                    <FormRow>
                      <FormGroup theme={darkMode ? 'dark' : 'light'}>
                        <label>Poin Sikap</label>
                        <input
                          type="text"
                          value={kompensasiData.appreciation_point}
                         
                          placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                        />
                      </FormGroup>
                      <FormGroup theme={darkMode ? 'dark' : 'light'}>
                        <label>Shift</label>
                        <select
                          value={kompensasiData.shift}
                          onChange={e => setKompensasiData({...kompensasiData, shift: e.target.value})}
                          required
                        >
                        <option value="">Pilih Shift</option>
                        <option value="Shift -">Shift -</option>
                        <option value="Shift 1">Shift 1: 07.00 – 14.30</option>
                        <option value="Shift 2">Shift 2: 14.30 – 22.00</option>
                        </select>
                      </FormGroup>
                    </FormRow>
                    
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Keterangan</label>
                      <textarea
                        value={kompensasiData.description}
                        onChange={e => setKompensasiData({...kompensasiData, description: e.target.value})}
                        placeholder="Masukkan keterangan tambahan..."
                        rows={4}
                        required
                      />
                    </FormGroup>
                    
                    <SubmitButton 
                      type="button" 
                      onClick={handleKompensasiSubmit}
                      disabled={submitting}
                    >
                      {submitting ? 'Menyimpan...' : 'Ajukan Laporan'}
                    </SubmitButton>
                  </>
                )}
                
                {activeTab === 'ketidakhadiran' && (
                  <>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Nama Siswa</label>
                      <input
                        type="text"
                        placeholder="Ketik & cari nama siswa..."
                        value={ketidakhadiranData.studentSearch || ''}
                        onChange={e => {
                          const value = e.target.value;
                          setKetidakhadiranData({ ...ketidakhadiranData, studentSearch: value });
                          setShowStudentSuggestions(true);
                        }}
                        onFocus={() => setShowStudentSuggestions(true)}
                        style={{ marginBottom: 8 }}
                      />
                      {showStudentSuggestions && ketidakhadiranData.studentSearch && (
                        <SuggestionsList theme={darkMode ? 'dark' : 'light'}>
                          {studentOptions.filter(opt =>
                            opt.name.toLowerCase().includes(ketidakhadiranData.studentSearch.toLowerCase()) &&
                            !ketidakhadiranData.selectedStudents.includes(opt.id)
                          ).map(opt => (
                            <StudentSuggestionItem
                              key={opt.id}
                              theme={darkMode ? 'dark' : 'light'}
                              onClick={() => {
                                addStudentToList(opt, 'ketidakhadiran');
                              }}
                            >
                              <span className="student-name">{opt.name}</span>{' '}
                              <span className="student-info">({opt.class})</span>
                            </StudentSuggestionItem>
                          ))}
                        </SuggestionsList>
                      )}
                      <SelectedStudentsContainer>
                        {ketidakhadiranData.selectedStudents.map((studentId, idx) => (
                          <SelectedStudentTag key={studentId}>
                            <span style={{ fontWeight: 500 }}>{getStudentNameById(studentId)}</span>
                            <button type="button" onClick={() => {
                              removeStudentFromList(studentId, 'ketidakhadiran');
                            }}>×</button>
                          </SelectedStudentTag>
                        ))}
                      </SelectedStudentsContainer>
                    </FormGroup>
                    
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Jenis Ketidakhadiran</label>
                      <select
                        value={ketidakhadiranData.absent_type}
                        onChange={e => {
                          const value = e.target.value;
                          let newTotalHour = '';
                          let newTotalHourKersos = '';
                          let newAppreciationPoint = '';
                          const normalized = (value || '').toLowerCase();
                          if (normalized === 'sakit' || normalized === 'ijin') {
                            // Class-based hour: XI/XII → -7, X → -5
                            newTotalHour = computePraktekTotalHourForAbsence(normalized, ketidakhadiranData.selectedStudents);
                            newTotalHourKersos = '0';
                            newAppreciationPoint = '0';
                          } else if (normalized === 'alfa') {
                            newTotalHour = '-14';
                            newTotalHourKersos = '-8';
                            newAppreciationPoint = '-40';
                          } else if (normalized === 'keterlambatan') {
                            newTotalHour = '0';
                            newTotalHourKersos = '0';
                            newAppreciationPoint = '0';
                          } else if (normalized === 'interupsi') {
                            newTotalHour = '0';
                            newTotalHourKersos = '0';
                            newAppreciationPoint = '0';
                          }
                          setKetidakhadiranData({
                            ...ketidakhadiranData,
                            absent_type: value,
                            jenisKetidakhadiran: value,
                            total_hour: newTotalHour,
                            total_hour_kersos: newTotalHourKersos,
                            appreciation_point: newAppreciationPoint
                          });
                        }}
                        required
                      >
                        <option value="">Pilih Jenis Ketidakhadiran</option>
                        <option value="sakit">Sakit</option>
                        <option value="ijin">Ijin</option>
                        <option value="alfa">Alfa</option>
                        <option value="keterlambatan">Keterlambatan</option>
                        <option value="interupsi">Interupsi</option>
                      </select>
                    </FormGroup>
                    
                    <FormRow>
                      <FormGroup theme={darkMode ? 'dark' : 'light'}>
                        <label>Section</label>
                        <select
                          value={ketidakhadiranData.section}
                          onChange={e => setKetidakhadiranData({...ketidakhadiranData, section: e.target.value})}
                          required
                        >
                          <option value="">Pilih Section</option>
                          <option value="LW">LW</option>
                          <option value="Umum">Umum</option>
                          <option value="Mill">Mill</option>
                          <option value="BW">BW</option>
                          <option value="CNC">CNC</option>
                          <option value="CNC/MILL">CNC/MILL</option>
                          <option value="GRIND">GRIND</option>
                        </select>
                      </FormGroup>
                      <FormGroup theme={darkMode ? 'dark' : 'light'}>
                        <label>Shift</label>
                        <select
                          value={ketidakhadiranData.shift}
                          onChange={e => setKetidakhadiranData({...ketidakhadiranData, shift: e.target.value})}
                          required
                        >
                          <option value="">Pilih Shift</option>
                        <option value="Shift -">Shift -</option>
                        <option value="Shift 1">Shift 1: 07.00 – 14.30</option>
                        <option value="Shift 2">Shift 2: 14.30 – 22.00</option>
                        </select>
                      </FormGroup>
                    </FormRow>
                    
                    <FormRow>
                      <FormGroup theme={darkMode ? 'dark' : 'light'}>
                        <label>Jumlah Jam Lembur (harus minus, contoh: -5)</label>
                        <input
                          type="number"
                          step="0.1"
                          value={ketidakhadiranData.total_hour}
                          onChange={e => setKetidakhadiranData({...ketidakhadiranData, total_hour: e.target.value})}
                          placeholder="Contoh: -5"
                        />
                      </FormGroup>
                      <FormGroup theme={darkMode ? 'dark' : 'light'}>
                        <label>Jumlah Jam Kersos</label>
                        <input
                          type="text"
                          value={ketidakhadiranData.total_hour_kersos}
                         
                          placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                        />
                      </FormGroup>
                    </FormRow>
                    
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Poin Sikap</label>
                      <input
                        type="text"
                        value={ketidakhadiranData.appreciation_point}
                       
                        placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                      />
                    </FormGroup>
                    
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Keterangan</label>
                      <textarea
                        value={ketidakhadiranData.description}
                        onChange={e => setKetidakhadiranData({...ketidakhadiranData, description: e.target.value})}
                        placeholder="Masukkan keterangan tambahan..."
                        rows={4}
                        required
                      />
                    </FormGroup>
                    
                    <SubmitButton 
                      type="button" 
                      onClick={handleKetidakhadiranSubmit}
                      disabled={submitting}
                    >
                      {submitting ? 'Menyimpan...' : 'Simpan Laporan'}
                    </SubmitButton>
                  </>
                )}
              </>
            )}

            {/* Form Teori */}
            {formMode === 'teori' && (
              <>
                <TypeToggleContainer>
                  <TypeToggleButtonGroup>
                    <TypeToggleButton active={activeTab === 'kompensasi'} onClick={() => setActiveTab('kompensasi')}>KOMPENSASI</TypeToggleButton>
                    <TypeToggleButton active={activeTab === 'ketidakhadiran'} onClick={() => setActiveTab('ketidakhadiran')}>KETIDAKHADIRAN</TypeToggleButton>
                  </TypeToggleButtonGroup>
                </TypeToggleContainer>
                
                {activeTab === 'kompensasi' && (
                  <>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Nama Siswa</label>
                      <input
                        type="text"
                        placeholder="Ketik & cari nama siswa..."
                        value={teoriData.studentSearch || ''}
                        onChange={e => {
                          const value = e.target.value;
                          setTeoriData({ ...teoriData, studentSearch: value });
                          setShowTeoriStudentSuggestions(true);
                        }}
                        onFocus={() => setShowTeoriStudentSuggestions(true)}
                        style={{ marginBottom: 8 }}
                      />
                      {showTeoriStudentSuggestions && teoriData.studentSearch && (
                        <SuggestionsList theme={darkMode ? 'dark' : 'light'}>
                          {studentOptions.filter(opt =>
                            opt.name.toLowerCase().includes(teoriData.studentSearch.toLowerCase()) &&
                            !teoriData.selectedStudents.includes(opt.id)
                          ).map(opt => (
                            <StudentSuggestionItem
                              key={opt.id}
                              theme={darkMode ? 'dark' : 'light'}
                              onClick={() => {
                                addStudentToList(opt, 'teori');
                              }}
                            >
                              <span className="student-name">{opt.name}</span>{' '}
                              <span className="student-info">({opt.class})</span>
                            </StudentSuggestionItem>
                          ))}
                        </SuggestionsList>
                      )}
                      <SelectedStudentsContainer>
                        {teoriData.selectedStudents.map((studentId, idx) => (
                          <SelectedStudentTag key={studentId}>
                            <span style={{ fontWeight: 500 }}>{getStudentNameById(studentId)}</span>
                            <button type="button" onClick={() => {
                              removeStudentFromList(studentId, 'teori');
                            }}>×</button>
                          </SelectedStudentTag>
                        ))}
                      </SelectedStudentsContainer>
                    </FormGroup>
                    
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Jenis Ketidaksesuaian</label>
                      <input
                        type="text"
                        placeholder="Ketik & cari jenis ketidaksesuaian..."
                        value={teoriData.jenisKetidaksesuaianSearch || ''}
                        onChange={e => {
                          const value = e.target.value;
                          setTeoriData({ 
                            ...teoriData, 
                            jenisKetidaksesuaianSearch: value,
                            jenisKetidaksesuaian: value,
                            kts_id: '',
                            total_hour: '',
                            total_hour_kersos: '',
                            appreciation_point: '',
                            additional_consequence: '',
                            section: ''
                          });
                          setShowTeoriJenisKetidaksesuaianSuggestions(true);
                        }}
                        onFocus={() => setShowTeoriJenisKetidaksesuaianSuggestions(true)}
                        required
                      />
                      {showTeoriJenisKetidaksesuaianSuggestions && teoriData.jenisKetidaksesuaianSearch && (
                        <SuggestionsList theme={darkMode ? 'dark' : 'light'}>
                          {getFilteredKtsOptions(teoriData.jenisKetidaksesuaianSearch).map(opt => (
                            <SuggestionItem
                              key={opt.id}
                              theme={darkMode ? 'dark' : 'light'}
                              onClick={() => {
                                handlePelanggaranSelection(opt, 'teori');
                                setShowTeoriJenisKetidaksesuaianSuggestions(false);
                              }}
                            >
                              {opt.description}
                            </SuggestionItem>
                          ))}
                        </SuggestionsList>
                      )}
                    </FormGroup>
                    
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Section</label>
                      <input
                        type="text"
                        value={teoriData.section}
                        readOnly
                        style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                      />
                    </FormGroup>
                    
                    <FormRow>
                      <FormGroup theme={darkMode ? 'dark' : 'light'}>
                        <label>Jumlah Jam Lembur</label>
                        <input
                          type="text"
                          value={teoriData.total_hour}
                          
                          placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                        />
                      </FormGroup>
                      <FormGroup theme={darkMode ? 'dark' : 'light'}>
                        <label>Jumlah Jam Kersos</label>
                        <input
                          type="text"
                          value={teoriData.total_hour_kersos}
                         
                          placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                        />
                      </FormGroup>
                    </FormRow>
                    
                    <FormRow>
                      <FormGroup theme={darkMode ? 'dark' : 'light'}>
                        <label>Poin Sikap</label>
                        <input
                          type="text"
                          value={teoriData.appreciation_point}
                         
                          placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                        />
                      </FormGroup>
                      <FormGroup theme={darkMode ? 'dark' : 'light'}>
                        <label>Konsekuensi Tambahan</label>
                        <input
                          type="text"
                          value={teoriData.additional_consequence}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                          placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                        />
                      </FormGroup>
                    </FormRow>
                    
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Keterangan</label>
                      <textarea
                        value={teoriData.description}
                        onChange={e => setTeoriData({...teoriData, description: e.target.value})}
                        placeholder="Masukkan keterangan tambahan..."
                        rows={4}
                        required
                      />
                    </FormGroup>
                    
                    <SubmitButton 
                      type="button" 
                      onClick={handleTeoriSubmit}
                      disabled={submitting}
                    >
                      {submitting ? 'Menyimpan...' : 'Simpan Laporan'}
                    </SubmitButton>
                  </>
                )}
                
                {activeTab === 'ketidakhadiran' && (
                  <>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Nama Siswa</label>
                      <input
                        type="text"
                        placeholder="Ketik & cari nama siswa..."
                        value={teoriKetidakhadiranData.studentSearch || ''}
                        onChange={e => {
                          const value = e.target.value;
                          setTeoriKetidakhadiranData({ ...teoriKetidakhadiranData, studentSearch: value });
                          setShowTeoriStudentSuggestions(true);
                        }}
                        onFocus={() => setShowTeoriStudentSuggestions(true)}
                        style={{ marginBottom: 8 }}
                      />
                      {showTeoriStudentSuggestions && teoriKetidakhadiranData.studentSearch && (
                        <SuggestionsList theme={darkMode ? 'dark' : 'light'}>
                          {studentOptions.filter(opt =>
                            opt.name.toLowerCase().includes(teoriKetidakhadiranData.studentSearch.toLowerCase()) &&
                            !teoriKetidakhadiranData.selectedStudents.includes(opt.id)
                          ).map(opt => (
                            <StudentSuggestionItem
                              key={opt.id}
                              theme={darkMode ? 'dark' : 'light'}
                              onClick={() => {
                                addStudentToList(opt, 'teori');
                              }}
                            >
                              <span className="student-name">{opt.name}</span>{' '}
                              <span className="student-info">({opt.class})</span>
                            </StudentSuggestionItem>
                          ))}
                        </SuggestionsList>
                      )}
                      <SelectedStudentsContainer>
                        {teoriKetidakhadiranData.selectedStudents.map((studentId, idx) => (
                          <SelectedStudentTag key={studentId}>
                            <span style={{ fontWeight: 500 }}>{getStudentNameById(studentId)}</span>
                            <button type="button" onClick={() => {
                              removeStudentFromList(studentId, 'teori');
                            }}>×</button>
                          </SelectedStudentTag>
                        ))}
                      </SelectedStudentsContainer>
                    </FormGroup>
                    
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Jenis Ketidakhadiran</label>
                      <select
                        value={teoriKetidakhadiranData.absent_type}
                        onChange={e => {
                          const value = e.target.value;
                          setTeoriKetidakhadiranData({
                            ...teoriKetidakhadiranData,
                            absent_type: value,
                            jenisKetidakhadiran: value,
                            total_hour: value === 'sakit' || value === 'ijin' ? '0' : value === 'alfa' ? '0' : '',
                          total_hour_kersos: value === 'sakit' || value === 'ijin' ? '0' : value === 'alfa' ? '-8' : '',
                          appreciation_point: value === 'sakit' || value === 'ijin' ? '0' : value === 'alfa' ? '-40' : ''
                          });
                        }}
                        required
                      >
                        <option value="">Pilih Jenis Ketidakhadiran</option>
                        <option value="sakit">Sakit</option>
                        <option value="ijin">Ijin</option>
                        <option value="alfa">Alfa</option>
                      </select>
                    </FormGroup>
                    
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Section</label>
                      <select
                        value={teoriKetidakhadiranData.section}
                        onChange={e => setTeoriKetidakhadiranData({...teoriKetidakhadiranData, section: e.target.value})}
                        required
                      >
                        <option value="">Pilih Section</option>
                         <option value="-">-</option>
                      </select>
                    </FormGroup>
                    
                    <FormRow>
                      <FormGroup theme={darkMode ? 'dark' : 'light'}>
                        <label>Jumlah Jam Lembur</label>
                        <input
                          type="text"
                          value={teoriKetidakhadiranData.total_hour}
                          onChange={e => setTeoriKetidakhadiranData({...teoriKetidakhadiranData, total_hour: e.target.value})}
                          
                          placeholder="Auto-filled based on violation"
                        />
                      </FormGroup>
                      <FormGroup theme={darkMode ? 'dark' : 'light'}>
                        <label>Jumlah Jam Kersos</label>
                        <input
                          type="text"
                          value={teoriKetidakhadiranData.total_hour_kersos}
                          onChange={e => setTeoriKetidakhadiranData({...teoriKetidakhadiranData, total_hour_kersos: e.target.value})}
                          
                          placeholder="Auto-filled based on violation"
                        />
                      </FormGroup>
                    </FormRow>
                    
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Poin Sikap</label>
                      <input
                        type="text"
                        value={teoriKetidakhadiranData.appreciation_point}
                        onChange={e => setTeoriKetidakhadiranData({...teoriKetidakhadiranData, appreciation_point: e.target.value})}
                        
                        placeholder="Auto-filled based on violation"
                      />
                    </FormGroup>
                    
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Keterangan</label>
                      <textarea
                        value={teoriKetidakhadiranData.description}
                        onChange={e => setTeoriKetidakhadiranData({...teoriKetidakhadiranData, description: e.target.value})}
                        placeholder="Masukkan keterangan tambahan..."
                        rows={4}
                        required
                      />
                    </FormGroup>
                    
                    <SubmitButton 
                      type="button" 
                      onClick={handleTeoriKetidakhadiranSubmit}
                      disabled={submitting}
                    >
                      {submitting ? 'Menyimpan...' : 'Simpan Laporan'}
                    </SubmitButton>
                  </>
                )}
              </>
            )}
          </FormContent>
          </ContentSection>
        </MainSection>
      </DashboardGrid>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  min-height: calc(100vh - 60px);
  background-color: #ffffff;
  color: #333333;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;



const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  order: 2;

  @media (max-width: 1100px) {
    order: 1;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const Subtitle = styled.p`
  color: var(--text-light);
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
`;

const ContentSection = styled.div`
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: var(--box-shadow);
  overflow: hidden;
`;

const FormHeader = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1.5rem 2rem;
`;

const FormContent = styled.div`
  padding: 2rem;
  background-color: #ffffff;
  color: #333333;
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border);
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const DetailGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  ${props => props.full ? 'grid-column: 1 / -1;' : ''}
`;

const DetailLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-light);
  margin-bottom: 0.5rem;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  background-color: #ffffff;
  color: #333333;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  background-color: #ffffff;
  color: #333333;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  background-color: #ffffff;
  color: #333333;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const TypeToggleContainer = styled.div`
  margin-bottom: 2rem;
`;

const TypeToggleButtonGroup = styled.div`
  display: flex;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
  background: var(--background-secondary);
`;

const TypeToggleButton = styled.button`
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: none;
  background: ${props => props.active ? '#2b5da1' : '#f5f5f5'};
  color: ${props => props.active ? 'white' : 'var(--text-dark)'};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? '#1a4c90' : '#e0e0e0'};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--text-light);
  }
  
  input, select, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid var(--border);
    border-radius: 6px;
    font-size: 1rem;
    background: #ffffff;
    color: #333333;
    transition: border-color 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: var(--primary);
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
    }
    
    &:read-only {
      background: var(--background-light);
      cursor: not-allowed;
    }
  }
  
  textarea {
    resize: vertical;
    min-height: 100px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SuggestionsList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${props => props.theme === 'dark' ? 'var(--background-dark)' : 'var(--background-light)'};
  border: 1px solid var(--border-color);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
`;

const SuggestionItem = styled.div`
  padding: 0.75rem;
  cursor: pointer;
  color: ${props => props.theme === 'dark' ? 'var(--text-dark)' : 'var(--text-light)'};
  transition: background-color 0.2s ease;
  
  &:hover {
    background: var(--background-hover);
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid var(--border-color);
  }
`;

const StudentSuggestionItem = styled(SuggestionItem)`
  .student-name {
    font-weight: 500;
  }
  
  .student-info {
    color: var(--text-secondary);
    font-size: 0.875rem;
  }
`;

const SelectedStudentsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const SelectedStudentTag = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--background-secondary);
  padding: 0.5rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
  
  button {
    background: none;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    font-size: 1.2rem;
    line-height: 1;
    padding: 0;
    
    &:hover {
      color: var(--danger-color);
    }
  }
`;

const SubmitButton = styled.button`
  background: #2b5da1;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.75rem 2rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #1a4c90;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(43, 93, 161, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: #ffffff;
  color: #333333;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--background-light);
    border-color: var(--primary);
  }
  
  &.primary {
    background: #2b5da1;
    color: white;
    border-color: #2b5da1;
    
    &:hover {
      background: #1a4c90;
    }
  }
  
  &.danger {
    background: var(--danger);
    color: white;
    border-color: var(--danger);
    
    &:hover {
      background: var(--danger-dark);
    }
  }
`;

// Sidebar styled components
const SidebarContainer = styled.div`
  @media (max-width: 1100px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  }
  
  @media (min-width: 1101px) {
    order: 1;
  }
`;

const SidebarOverlay = styled.div`
  display: none;
  
  @media (max-width: 1100px) {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    opacity: ${props => props.$isOpen ? 1 : 0};
    transition: opacity 0.3s ease;
    pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  }
`;

const SidebarContent = styled.div`
  @media (max-width: 1100px) {
    position: absolute;
    top: 0;
    left: 0;
    width: 280px;
    height: 100vh;
    background-color: var(--background);
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
    transform: translateX(${props => props.$isOpen ? '0' : '-100%'});
    transition: transform 0.3s ease;
    overflow-y: auto;
    z-index: 1001;
  }

  @media (max-width: 576px) {
    width: 260px;
  }
`;

const SidebarHeader = styled.div`
  display: none;

  @media (max-width: 1100px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid var(--border);
    background-color: var(--primary);
    color: white;

    h3 {
      margin: 0;
      font-size: 1.125rem;
      font-weight: 600;
    }
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;





export default AdminLaporKTSPage;